import styled, { css } from 'styled-components';
import { m } from 'framer-motion';

import media from '../../../common/MediaQueries';
import { transition } from '../../../common/mixins';
import Button from '../../atoms/Button';
import Link from '../../atoms/Link';
import { FlexBox, Inner } from '../../styles/Grid/styles';
import Typography from '../../styles/Typography';

// Main banner container
export const CookieConsentBanner = styled(m.div)`
  ${transition('all', '280ms')}
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  opacity: 0;
  transform: translateY(100%);
  background-color: ${({ theme }) => theme.colors.neutral.neutral10};
  ${({ theme }) => theme.shadows.banner};
  border-top: 1px solid ${({ theme }) => theme.colors.neutral.neutral30};

  @media ${media.maxTablet} {
    position: fixed;
    height: 100vh;
    overflow-y: auto;
    background-color: ${({ theme }) => theme.colors.neutral.neutral10};
  }
`;

export const CookieConsentInner = styled(Inner)`
  padding: 2rem;

  @media ${media.maxTablet} {
    padding: 1.5rem;
  }
`;

export const CookieConsentContent = styled(FlexBox)`
  flex-direction: column;
  gap: 1.5rem;

  @media ${media.tablet} {
    flex-direction: row;
    align-items: flex-start;
    gap: 2rem;
  }
`;

export const CookieConsentText = styled.div`
  flex: 1;
`;

export const CookieConsentTitle = styled(Typography)`
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.neutral.neutral100};
`;

export const CookieConsentDescription = styled.div`
  color: ${({ theme }) => theme.colors.additional.accentGray2};
  line-height: 1.5;

  p {
    margin-bottom: 1rem;
  }

  a {
    color: ${({ theme }) => theme.colors.primary.main};
    text-decoration: underline;
    
    &:hover {
      color: ${({ theme }) => theme.colors.primary.hover};
    }
  }
`;

export const CookieConsentActions = styled(FlexBox)`
  flex-direction: column;
  gap: 0.75rem;
  min-width: 250px;

  @media ${media.phone} {
    flex-direction: row;
    gap: 0.75rem;
    align-items: center;
  }

  @media ${media.maxTablet} {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

export const CookieConsentButton = styled(Button)`
  white-space: nowrap;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
`;

export const CookieConsentPrimaryButton = styled(CookieConsentButton)`
  background-color: ${({ theme }) => theme.colors.primary.main};
  color: ${({ theme }) => theme.colors.neutral.neutral10};
  border: 1px solid ${({ theme }) => theme.colors.primary.main};

  &:hover {
    background-color: ${({ theme }) => theme.colors.primary.hover};
    border-color: ${({ theme }) => theme.colors.primary.hover};
  }
`;

export const CookieConsentSecondaryButton = styled(CookieConsentButton)`
  background-color: transparent;
  color: ${({ theme }) => theme.colors.neutral.neutral80};
  border: 1px solid ${({ theme }) => theme.colors.neutral.neutral50};

  &:hover {
    background-color: ${({ theme }) => theme.colors.neutral.neutral20};
  }
`;

export const CookieConsentLinks = styled(FlexBox)`
  gap: 1rem;
  margin-top: 0.5rem;
  
  @media ${media.maxPhone} {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

export const CookieConsentLink = styled(Link)`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.additional.accentGray2};
  text-decoration: underline;

  &:hover {
    color: ${({ theme }) => theme.colors.primary.main};
  }
`;

// Settings Modal
export const CookieSettingsModal = styled(m.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1001;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;

  @media ${media.maxTablet} {
    padding: 0;
    align-items: flex-start;
  }
`;

export const CookieSettingsContent = styled(m.div)`
  background-color: ${({ theme }) => theme.colors.neutral.neutral10};
  border-radius: 0.5rem;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  padding: 2rem;

  @media ${media.maxTablet} {
    max-height: 100vh;
    border-radius: 0;
    height: 100%;
  }

  @media ${media.maxPhone} {
    padding: 1.5rem;
  }
`;

export const CookieSettingsHeader = styled(FlexBox)`
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutral.neutral30};
`;

export const CookieSettingsTitle = styled(Typography)`
  color: ${({ theme }) => theme.colors.neutral.neutral100};
`;

export const CookieSettingsClose = styled(Button)`
  padding: 0.5rem;
  font-size: 1.5rem;
  color: #000000;
  background: none;
  border: none;
  border-radius: 0;
  box-shadow: none;
  position: relative;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 20px;
    height: 2px;
    background-color: #000000;
    top: 50%;
    left: 50%;
  }

  &::before {
    transform: translate(-50%, -50%) rotate(45deg);
  }

  &::after {
    transform: translate(-50%, -50%) rotate(-45deg);
  }

  svg {
    display: none;
  }

  &:hover {
    background: none;
    color: #666666;
    
    &::before,
    &::after {
      background-color: #666666;
    }
  }

  &:focus {
    outline: 2px solid #000000;
    outline-offset: 2px;
  }
`;

export const CookieCategory = styled.div`
  margin-bottom: 2rem;
  padding: 1.5rem;
  border: 1px solid ${({ theme }) => theme.colors.neutral.neutral30};
  border-radius: 0.5rem;
  background-color: ${({ theme }) => theme.colors.neutral.neutral20};
`;

export const CookieCategoryHeader = styled(FlexBox)`
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  gap: 1rem;
`;

export const CookieCategoryInfo = styled.div`
  flex: 1;
`;

export const CookieCategoryName = styled(Typography)`
  margin-bottom: 0.5rem;
  color: ${({ theme }) => theme.colors.neutral.neutral100};
`;

export const CookieCategoryDescription = styled.p`
  color: ${({ theme }) => theme.colors.additional.accentGray2};
  line-height: 1.5;
  margin: 0;
`;

export const CookieCategoryToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const CookieToggleSwitch = styled.label<{ disabled?: boolean }>`
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.5;
      cursor: not-allowed;
    `}
`;

export const CookieToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + span {
    background-color: ${({ theme }) => theme.colors.primary.main};
  }

  &:checked + span:before {
    transform: translateX(26px);
  }
`;

export const CookieToggleSlider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ theme }) => theme.colors.neutral.neutral50};
  transition: 0.2s;
  border-radius: 24px;

  &:before {
    position: absolute;
    content: '';
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: 0.2s;
    border-radius: 50%;
  }
`;

export const CookieDetailsTable = styled.table`
  width: 100%;
  margin-top: 1rem;
  border-collapse: collapse;
  font-size: 0.875rem;

  th,
  td {
    padding: 0.75rem;
    text-align: left;
    border-bottom: 1px solid ${({ theme }) => theme.colors.neutral.neutral30};
  }

  th {
    background-color: ${({ theme }) => theme.colors.neutral.neutral20};
    font-weight: 600;
    color: ${({ theme }) => theme.colors.neutral.neutral100};
  }

  td {
    color: ${({ theme }) => theme.colors.additional.accentGray2};
  }
`;

export const CookieSettingsActions = styled(FlexBox)`
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid ${({ theme }) => theme.colors.neutral.neutral30};

  @media ${media.maxPhone} {
    flex-direction: column;
  }
`;

export const CookieSettingsDisclaimer = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background-color: ${({ theme }) => theme.colors.neutral.neutral20};
  border-radius: 0.25rem;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.additional.accentGray2};
  line-height: 1.5;
`;
